import { useEffect } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/Breadcrumb";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import ServiceFAQ from "@/components/ServiceFAQ";
import { generateLocalBusinessSchema } from "@/lib/schemaGenerator";
import { Wine, Clock, MapPin, CheckCircle2, Star, Users } from "lucide-react";
import wineTours from "@/assets/wine-tours.png";
import audiLuxury from "@/assets/audi-luxury.jpg";
import wineToursBackground from "@/assets/wine-tours-background.jpg";
import wineVineyardSunset from "@/assets/wine-vineyard-sunset.jpeg";
import wineCelebration from "@/assets/wine-celebration.jpeg";
const WineTours = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Texas Hill Country Wine Tours FAQs - Optimized for high-intent keywords
  const faqs = [
    {
      question: "How much does a Texas Hill Country Wine Tour cost?",
      answer: "Our Texas Hill Country Wine Tours offer transparent hourly pricing at $100/hour with a 4-hour minimum for half-day tours. Full-day Wine Tour Transportation to Fredericksburg typically runs 6-8 hours. We provide upfront quotes with no hidden fees—the price includes your professional chauffeur and luxury SUV transportation."
    },
    {
      question: "How many wineries can we visit on a Fredericksburg Wine Tour?",
      answer: "Our Fredericksburg Wine Tours typically include 2-3 wineries for half-day tours (4 hours) and 4-5 wineries for full-day tours (6-8 hours). Your Texas Hill Country Wine Tour is fully customizable—we'll help plan the perfect itinerary based on your preferences and time."
    },
    {
      question: "Is your Wine Tour Transportation a safe designated driver service?",
      answer: "Absolutely! Our Wine Tour Transportation provides a professional designated driver so you can enjoy Texas Hill Country wineries worry-free. Your chauffeur handles all driving on scenic Hill Country roads while you savor the Fredericksburg Wine Tour experience safely."
    },
    {
      question: "What areas do you serve for Texas Hill Country Wine Tours?",
      answer: "Our Wine Tour Transportation covers all major Texas Hill Country wine regions including Fredericksburg (290 Wine Trail), Comfort, Sisterdale, Stonewall, and New Braunfels wine country. We pick up throughout San Antonio and transport you to the best Hill Country wineries."
    },
    {
      question: "How far in advance should I book a Fredericksburg Wine Tour?",
      answer: "We recommend booking your Texas Hill Country Wine Tour 1-2 weeks in advance, especially for weekends during peak season (spring and fall). However, our Wine Tour Transportation often accommodates last-minute requests—call (210) 296-7409 to check availability for your Fredericksburg Wine Tour."
    }
  ];
  const wineryDestinations = [{
    name: "Texas Hill Country Wine Trail",
    distance: "45-90 minutes from San Antonio",
    wineries: "40+ award-winning wineries"
  }, {
    name: "Fredericksburg Wine Region",
    distance: "75 minutes from San Antonio",
    wineries: "50+ tasting rooms and vineyards"
  }, {
    name: "Comfort & Sisterdale",
    distance: "60 minutes from San Antonio",
    wineries: "Boutique wineries and scenic views"
  }, {
    name: "New Braunfels Wine Country",
    distance: "35 minutes from San Antonio",
    wineries: "Hidden gems along the Guadalupe"
  }];
  const tourFeatures = [{
    icon: <Wine className="w-8 h-8" />,
    title: "Curated Wine Experiences",
    description: "Visit hand-selected wineries featuring award-winning Texas wines"
  }, {
    icon: <Users className="w-8 h-8" />,
    title: "Private Group Tours",
    description: "Spacious 2025 Audi Q7 accommodates up to 6 guests comfortably"
  }, {
    icon: <Clock className="w-8 h-8" />,
    title: "Flexible Scheduling",
    description: "Choose 4, 6, or 8-hour tours customized to your preferences"
  }, {
    icon: <MapPin className="w-8 h-8" />,
    title: "Local Expertise",
    description: "Professional chauffeur familiar with all Hill Country routes"
  }];
  const tourInclusions = ["Door-to-door luxury transportation", "Professional chauffeur service", "Complimentary bottled water", "Ice cooler for wine purchases", "Bluetooth audio system", "Flexible itinerary planning", "Photo stops at scenic locations", "Climate-controlled comfort"];
  
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://ricksblackcarservicesantonio.com/services/wine-tours#service",
    "name": "Private Fredericksburg Wine Tours from San Antonio",
    "serviceType": "Wine Tour Transportation",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://ricksblackcarservicesantonio.com/#business",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "url": "https://ricksblackcarservicesantonio.com"
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "Fredericksburg",
        "addressRegion": "Texas"
      },
      {
        "@type": "City",
        "name": "San Antonio",
        "addressRegion": "Texas"
      },
      {
        "@type": "City",
        "name": "Comfort",
        "addressRegion": "Texas"
      },
      {
        "@type": "City",
        "name": "New Braunfels",
        "addressRegion": "Texas"
      },
      {
        "@type": "Region",
        "name": "Texas Hill Country"
      }
    ],
    "description": "Private 6-hour or 8-hour dedicated chauffeur service to the Fredericksburg 290 Wine Trail in our 2025 Audi Q7. We handle all the driving so you can enjoy the best Texas Hill Country vineyards safely and in luxury. Perfect for groups up to 6 passengers.",
    "image": [
      "https://ricksblackcarservicesantonio.com/og-image.jpg"
    ],
    "category": "Wine Tour Transportation",
    "serviceOutput": "Safe, luxurious transportation to Hill Country wineries with professional chauffeur",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Wine Tour Packages",
      "itemListElement": [
        {
          "@type": "Offer",
          "name": "4-Hour Half-Day Wine Tour",
          "description": "Visit 2-3 wineries in Fredericksburg or nearby regions with professional chauffeur service",
          "price": "540",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "itemOffered": {
            "@type": "Service",
            "name": "4-Hour Half-Day Wine Tour"
          }
        },
        {
          "@type": "Offer",
          "name": "6-Hour Extended Wine Tour",
          "description": "Visit 3-4 wineries with lunch break and flexible schedule throughout Hill Country",
          "price": "810",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "itemOffered": {
            "@type": "Service",
            "name": "6-Hour Extended Wine Tour"
          }
        },
        {
          "@type": "Offer",
          "name": "8-Hour Full-Day Wine Tour",
          "description": "Visit 4-5 wineries with extended time for lunch, shopping, and scenic photo stops",
          "price": "1080",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "itemOffered": {
            "@type": "Service",
            "name": "8-Hour Full-Day Wine Tour"
          }
        }
      ]
    },
    "offers": {
      "@type": "AggregateOffer",
      "url": "https://ricksblackcarservicesantonio.com/services/wine-tours",
      "priceCurrency": "USD",
      "lowPrice": "540",
      "highPrice": "1080",
      "offerCount": "3",
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock",
      "validFrom": "2024-01-01",
      "seller": {
        "@type": "LocalBusiness",
        "name": "Rick's Black Car Service",
        "telephone": "+1-210-296-7409"
      }
    },
    "priceRange": "$135/hour (4-hour minimum)",
    "additionalType": "https://schema.org/TaxiService",
    "audience": {
      "@type": "Audience",
      "audienceType": "Wine Enthusiasts, Couples, Groups, Bachelorette Parties"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "servicePhone": {
        "@type": "ContactPoint",
        "telephone": "+1-210-296-7409",
        "contactType": "Reservations"
      },
      "serviceUrl": "https://ricksblackcarservicesantonio.com/services/wine-tours"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const imageGallerySchema = {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    "name": "Texas Hill Country Wine Tours Gallery",
    "description": "Professional photos of luxury wine tour transportation and Texas Hill Country wineries from Rick's Black Car Service",
    "image": [
      {
        "@type": "ImageObject",
        "url": "https://ricksblackcarservicesantonio.com/assets/wine-tours.png",
        "caption": "Rick's Black Car Service luxury wine tour transportation to Texas Hill Country",
        "description": "Premium black car service providing safe and elegant wine tour transportation from San Antonio to Fredericksburg and Hill Country wineries",
        "width": 1920,
        "height": 1080,
        "publisher": {
          "@type": "Organization",
          "name": "Rick's Black Car Service"
        }
      },
      {
        "@type": "ImageObject",
        "url": "https://ricksblackcarservicesantonio.com/assets/wine-vineyard-sunset.jpeg",
        "caption": "Texas Hill Country vineyard at sunset during wine tour",
        "description": "Beautiful sunset views at Texas Hill Country vineyards visited during Rick's luxury wine tours from San Antonio",
        "width": 1920,
        "height": 1080,
        "publisher": {
          "@type": "Organization",
          "name": "Rick's Black Car Service"
        }
      },
      {
        "@type": "ImageObject",
        "url": "https://ricksblackcarservicesantonio.com/assets/wine-celebration.jpeg",
        "caption": "Wine tasting celebration at Texas Hill Country winery",
        "description": "Guests enjoying wine tasting experience during luxury wine tour with Rick's Black Car Service transportation",
        "width": 1920,
        "height": 1080,
        "publisher": {
          "@type": "Organization",
          "name": "Rick's Black Car Service"
        }
      }
    ]
  };

  return <div className="min-h-screen">
      <SEO title="Private Fredericksburg Wine Tours from San Antonio | Rick's Black Car Service" description="Private Fredericksburg wine tours from San Antonio. Relax in our luxury Audi Q7 while we handle the driving to the best Texas Hill Country vineyards." keywords="fredericksburg wine tours, 290 wine trail, san antonio wine tour transportation, texas hill country wine tours, private wine tour chauffeur, designated driver wine tour" canonicalPath="/services/wine-tours" schema={[serviceSchema, faqSchema, imageGallerySchema]} />

      <Navbar />
      
      <main className="pt-20">
        <Breadcrumb 
          items={[
            { name: "Services", url: "/services" },
            { name: "Wine Tours", url: "/services/wine-tours" }
          ]}
        />

        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${wineTours})`
        }}>
            <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/70 to-background/90" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">
                290 Wine Trail Experience
              </span>
              <h1 className="text-5xl md:text-6xl font-serif font-bold text-foreground">
                Private Fredericksburg Wine Tours from San Antonio
              </h1>
              <p className="text-xl text-foreground/80 leading-relaxed">
                Experience the best of the Texas Hill Country with a 6-hour or 8-hour dedicated chauffeur service in our luxurious 2025 Audi Q7. We handle all the driving on the scenic 290 Wine Trail so you and your guests can relax, sip, and savor the finest vineyards in the region—safely and in complete luxury.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="http://ricksblackcarservicesantonio.square.site" target="_blank" rel="noopener noreferrer">
                    Book Your Private Executive Car Service Today
                  </a>
                </Button>
                <Button asChild variant="outline" className="text-lg px-8 py-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                  <a href="tel:210-296-7409">Call (210) 296-7409</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-20 luxury-gradient">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Our <strong className="text-foreground">Texas Hill Country Wine Tours</strong> are perfect for couples, small groups, bachelorette parties, birthdays, and corporate outings. As your trusted <strong className="text-foreground">Fredericksburg Wine Tour</strong> provider, we customize each tour to your preferences, whether you want to visit specific wineries or need recommendations. Enjoy safe <strong className="text-foreground">wine tour transportation</strong> with a professional designated driver through the scenic Texas wine country.
              </p>
            </div>
          </div>
        </section>

        {/* Wine Tour Services Section */}
        <section className="py-20 bg-secondary">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground text-center mb-12">
                Professional Fredericksburg Wine Tour Transportation Services
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">Fredericksburg Wine Tours</h3>
                    <p className="text-muted-foreground">Visit the best wineries in Fredericksburg</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">Custom Winery Itineraries</h3>
                    <p className="text-muted-foreground">We&apos;ll plan your route or follow your preferences</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">Multi-Winery Tours</h3>
                    <p className="text-muted-foreground">Visit 3-5 wineries in one day</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">Private Tours</h3>
                    <p className="text-muted-foreground">Exclusive service for your group</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">Group Wine Tours</h3>
                    <p className="text-muted-foreground">Accommodate parties of various sizes</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">Bachelorette Wine Tours</h3>
                    <p className="text-muted-foreground">Celebrate in style with safe transportation</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">Corporate Wine Events</h3>
                    <p className="text-muted-foreground">Team building and client entertainment</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">Birthday & Anniversary Tours</h3>
                    <p className="text-muted-foreground">Make special occasions memorable</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">Scenic Hill Country Routes</h3>
                    <p className="text-muted-foreground">Enjoy the beautiful Texas landscape</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">Lunch Reservations</h3>
                    <p className="text-muted-foreground">We can coordinate dining at Hill Country restaurants</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="py-20 luxury-gradient">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-serif font-bold mt-2 text-foreground">
                Why Choose Our Wine Tours
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <Card className="card-luxury">
                <CardContent className="p-8 space-y-4">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                  <h3 className="text-xl font-semibold text-foreground">Safe, Designated Driver</h3>
                  <p className="text-muted-foreground">Enjoy wine tasting worry-free</p>
                </CardContent>
              </Card>
              <Card className="card-luxury">
                <CardContent className="p-8 space-y-4">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                  <h3 className="text-xl font-semibold text-foreground">Luxury Vehicles</h3>
                  <p className="text-muted-foreground">Comfortable rides through Hill Country</p>
                </CardContent>
              </Card>
              <Card className="card-luxury">
                <CardContent className="p-8 space-y-4">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                  <h3 className="text-xl font-semibold text-foreground">Flexible Itineraries</h3>
                  <p className="text-muted-foreground">Visit wineries at your own pace</p>
                </CardContent>
              </Card>
              <Card className="card-luxury">
                <CardContent className="p-8 space-y-4">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                  <h3 className="text-xl font-semibold text-foreground">Local Expertise</h3>
                  <p className="text-muted-foreground">Recommendations for best wineries and hidden gems</p>
                </CardContent>
              </Card>
              <Card className="card-luxury">
                <CardContent className="p-8 space-y-4">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                  <h3 className="text-xl font-semibold text-foreground">Complimentary Water</h3>
                  <p className="text-muted-foreground">Stay hydrated throughout your tour</p>
                </CardContent>
              </Card>
              <Card className="card-luxury">
                <CardContent className="p-8 space-y-4">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                  <h3 className="text-xl font-semibold text-foreground">Cooler Available</h3>
                  <p className="text-muted-foreground">Bring home your wine purchases safely</p>
                </CardContent>
              </Card>
              <Card className="card-luxury">
                <CardContent className="p-8 space-y-4">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                  <h3 className="text-xl font-semibold text-foreground">Photo Stops</h3>
                  <p className="text-muted-foreground">Capture memories at scenic Hill Country locations</p>
                </CardContent>
              </Card>
              <Card className="card-luxury">
                <CardContent className="p-8 space-y-4">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                  <h3 className="text-xl font-semibold text-foreground">Restaurant Recommendations</h3>
                  <p className="text-muted-foreground">We know the best Hill Country dining</p>
                </CardContent>
              </Card>
              <Card className="card-luxury">
                <CardContent className="p-8 space-y-4">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                  <h3 className="text-xl font-semibold text-foreground">No Rush</h3>
                  <p className="text-muted-foreground">Take your time at each winery</p>
                </CardContent>
              </Card>
              <Card className="card-luxury">
                <CardContent className="p-8 space-y-4">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                  <h3 className="text-xl font-semibold text-foreground">Professional Service</h3>
                  <p className="text-muted-foreground">Courteous, knowledgeable chauffeurs</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Destinations Section */}
        <section className="relative py-20 overflow-hidden bg-secondary">
          <div className="absolute inset-0 bg-cover bg-center opacity-35 brightness-150" style={{
          backgroundImage: `url(${wineToursBackground})`
        }}>
            <div className="absolute inset-0 bg-gradient-to-b from-background/35 via-background/25 to-background/35" />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-serif font-bold mt-2 text-foreground">
                Featured Wine Regions We Serve
              </h2>
            </div>

            <div className="max-w-4xl mx-auto space-y-6">
              <Card className="card-luxury">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-serif font-bold text-foreground mb-3">Fredericksburg Wine Country</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    50+ wineries in the heart of Hill Country. The premier wine destination in Texas with world-class wineries and tasting rooms.
                  </p>
                </CardContent>
              </Card>

              <Card className="card-luxury">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-serif font-bold text-foreground mb-3">290 Wine Trail</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    Scenic route with numerous tasting rooms. Beautiful highway between Fredericksburg and Johnson City with stunning Hill Country views.
                  </p>
                </CardContent>
              </Card>

              <Card className="card-luxury">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-serif font-bold text-foreground mb-3">Comfort Wine Region</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    Boutique wineries and charming towns. Closer to San Antonio, perfect for shorter tours with authentic Hill Country charm.
                  </p>
                </CardContent>
              </Card>

              <Card className="card-luxury">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-serif font-bold text-foreground mb-3">Dripping Springs Wine Trail</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    Emerging wine destination with innovative wineries and beautiful vineyard settings between Austin and Hill Country.
                  </p>
                </CardContent>
              </Card>

              <Card className="card-luxury">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-serif font-bold text-foreground mb-3">Johnson City Area</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    Historic wineries and beautiful vineyards. Rich wine heritage with family-owned operations and stunning Texas landscapes.
                  </p>
                </CardContent>
              </Card>

              <Card className="card-luxury">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-serif font-bold text-foreground mb-3">Custom Routes</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    Create your own wine adventure. We can customize any route to match your specific winery preferences and interests.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Popular Wineries Section */}
        <section className="py-20 luxury-gradient">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground text-center mb-6">
                Popular Wineries We Visit
              </h2>
              <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
                We provide transportation to all Texas Hill Country wineries including:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <Card className="card-luxury">
                  <CardContent className="p-4 text-center">
                    <p className="text-foreground font-medium">Becker Vineyards</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-4 text-center">
                    <p className="text-foreground font-medium">Grape Creek Vineyards</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-4 text-center">
                    <p className="text-foreground font-medium">Pedernales Cellars</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-4 text-center">
                    <p className="text-foreground font-medium">William Chris Vineyards</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-4 text-center">
                    <p className="text-foreground font-medium">Messina Hof Fredericksburg</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-4 text-center">
                    <p className="text-foreground font-medium">4.0 Cellars</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-4 text-center">
                    <p className="text-foreground font-medium">Duchman Family Winery</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-4 text-center">
                    <p className="text-foreground font-medium">Flat Creek Estate</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-4 text-center">
                    <p className="text-foreground font-medium">Wedding Oak Winery</p>
                  </CardContent>
                </Card>
                <Card className="card-luxury">
                  <CardContent className="p-4 text-center">
                    <p className="text-foreground font-medium">And 100+ more!</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center opacity-30 brightness-150" style={{
          backgroundImage: `url(${wineVineyardSunset})`
        }}>
            <div className="absolute inset-0 bg-gradient-to-b from-background/25 via-background/15 to-background/25" />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-serif font-bold mt-2 text-foreground">
                  What's Included
                </h2>
              </div>

              <Card className="card-luxury mb-8">
                <CardContent className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-foreground">Professional chauffeur</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-foreground">Luxury transportation</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-foreground">Flexible itinerary</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-foreground">Complimentary water</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-foreground">Climate control</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-foreground">Scenic Hill Country drive</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-foreground">Local recommendations</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="text-center">
                <p className="text-muted-foreground mb-4">
                  <strong className="text-foreground">You provide:</strong> Winery selections, tasting fees, lunch, gratuity (appreciated).
                </p>
                <p className="text-muted-foreground">
                  Call (210) 296-7409 to book your Texas wine tour today!
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Tour Packages */}
        <section className="relative py-20 overflow-hidden bg-secondary">
          <div className="absolute inset-0 bg-cover bg-center opacity-30 brightness-150" style={{
          backgroundImage: `url(${wineCelebration})`
        }}>
            <div className="absolute inset-0 bg-gradient-to-b from-secondary/30 via-secondary/20 to-secondary/30" />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-serif font-bold mt-2 text-foreground">
                Wine Tour Packages
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <Card className="card-luxury hover:scale-105 transition-transform duration-300">
                <CardContent className="p-8 space-y-4">
                  <h3 className="text-2xl font-serif font-bold text-foreground">Half-Day Tour</h3>
                  <p className="text-3xl font-bold text-primary">4 Hours</p>
                  <p className="text-muted-foreground">Visit 2-3 wineries, perfect for a quick getaway</p>
                </CardContent>
              </Card>

              <Card className="card-luxury hover:scale-105 transition-transform duration-300">
                <CardContent className="p-8 space-y-4">
                  <h3 className="text-2xl font-serif font-bold text-foreground">Full-Day Tour</h3>
                  <p className="text-3xl font-bold text-primary">8 Hours</p>
                  <p className="text-muted-foreground">Comprehensive experience with 4-5 wineries and lunch</p>
                </CardContent>
              </Card>

              <Card className="card-luxury hover:scale-105 transition-transform duration-300">
                <CardContent className="p-8 space-y-4">
                  <h3 className="text-2xl font-serif font-bold text-foreground">Extended Tour</h3>
                  <p className="text-3xl font-bold text-primary">10+ Hours</p>
                  <p className="text-muted-foreground">Leisurely pace with maximum exploration</p>
                </CardContent>
              </Card>

              <Card className="card-luxury hover:scale-105 transition-transform duration-300">
                <CardContent className="p-8 space-y-4">
                  <h3 className="text-2xl font-serif font-bold text-foreground">Custom Tours</h3>
                  <p className="text-3xl font-bold text-primary">Flexible</p>
                  <p className="text-muted-foreground">Design your perfect wine country experience</p>
                </CardContent>
              </Card>

              <Card className="card-luxury hover:scale-105 transition-transform duration-300">
                <CardContent className="p-8 space-y-4">
                  <h3 className="text-2xl font-serif font-bold text-foreground">Hourly Service</h3>
                  <p className="text-3xl font-bold text-primary">By the Hour</p>
                  <p className="text-muted-foreground">Book by the hour for ultimate flexibility</p>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-12">
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                All tours include round-trip luxury transportation from San Antonio or surrounding areas. Contact us for pricing and to plan your perfect wine tour.
              </p>
            </div>
          </div>
        </section>

        {/* What to Expect Section */}
        <section className="py-20 luxury-gradient">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground text-center mb-12">
                What to Expect
              </h2>
              <div className="space-y-6">
                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary text-primary-foreground rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                        1
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-2">Pickup</h3>
                        <p className="text-muted-foreground">We pick you up from your San Antonio location</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary text-primary-foreground rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                        2
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-2">Scenic Drive</h3>
                        <p className="text-muted-foreground">Enjoy the beautiful 60-90 minute drive to Hill Country</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary text-primary-foreground rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                        3
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-2">Winery Visits</h3>
                        <p className="text-muted-foreground">Spend 45-60 minutes at each winery tasting and exploring</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary text-primary-foreground rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                        4
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-2">Lunch Break</h3>
                        <p className="text-muted-foreground">Stop at a Hill Country restaurant (optional)</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary text-primary-foreground rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                        5
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-2">More Wineries</h3>
                        <p className="text-muted-foreground">Continue your tasting adventure</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary text-primary-foreground rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                        6
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-2">Return Trip</h3>
                        <p className="text-muted-foreground">Relax on the comfortable ride back to San Antonio</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary text-primary-foreground rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                        7
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-2">Safe Arrival</h3>
                        <p className="text-muted-foreground">Dropped off at your door, worry-free</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 luxury-gradient">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <ServiceFAQ faqs={faqs} />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative overflow-hidden bg-secondary">
          <div className="absolute inset-0 z-0">
            <div className="relative w-full h-full">
              <img src={audiLuxury} alt="Luxury wine tour transportation" className="w-full h-full object-cover opacity-90 brightness-125" />
              <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/45 to-background/50" />
            </div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <Card className="card-luxury bg-card/90 backdrop-blur-sm border-primary/20">
              <CardContent className="p-12 text-center space-y-6">
                <h2 className="text-4xl font-serif font-bold text-foreground drop-shadow-lg">
                  Book Your Texas Hill Country Wine Tour Today
                </h2>
                <p className="text-foreground/90 text-lg max-w-3xl mx-auto drop-shadow-md">
                  Call <a href="tel:210-296-7409" className="text-primary font-semibold hover:underline">210-296-7409</a> or email <a href="mailto:ricksblackcarservice@outlook.com" className="text-primary font-semibold hover:underline">ricksblackcarservice@outlook.com</a> for custom wine tour packages and availability. Experience the best of Texas wine country with safe, luxury transportation from San Antonio.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button className="btn-luxury text-lg px-8 py-6" asChild>
                    <a href="http://ricksblackcarservicesantonio.square.site" target="_blank" rel="noopener noreferrer">
                      Book Your Wine Tour
                    </a>
                  </Button>
                  <Button asChild variant="outline" className="text-lg px-8 py-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                    <a href="tel:210-296-7409">Call (210) 296-7409</a>
                  </Button>
                  <Button asChild variant="outline" className="text-lg px-8 py-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                    <a href="mailto:ricksblackcarservice@outlook.com">Email Us</a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Instant Quote CTA */}
        <InstantQuoteCTA />
      </main>

      <Footer />
    </div>;
};
export default WineTours;